import React, { useEffect, useState, useCallback } from "react";
import {
    Button,
    Form,
    FormGroup,
    Spinner,
    Stack,
} from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import RTE from "../components/RTE/RTE";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSave, faBan, faPaperPlane } from "@fortawesome/free-solid-svg-icons";
import { API } from '../data/API/API';
import Switch from "react-switch";
import { sendTestEmail } from "../data/API/Emails";
import "./CreateEmailTemplatePage.scss";

interface EmailTemplate {
    id: number;
    templateName: string;
    header: string;
    body: string;
    active: boolean;
}

const CreateEmailTemplatePage: React.FC = () => {
    const [isLoading, setLoading] = useState(true);
    const [isSaving, setSaving] = useState(false);
    const [message, setMessage] = useState<{ text: string; type: 'error' | 'success' }>({ text: "", type: 'error' });
    const [emailTemplate, setEmailTemplate] = useState<EmailTemplate>({
        id: -1,
        templateName: "",
        header: "",
        body: "",
        active: false
    });

    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const init = useCallback(async (id?: string) => {
        if (id) {
            try {
                const template = await API.EmailTemplates.GetEmailTemplateById(id);
                if (template) {
                    setEmailTemplate({
                        id: template.id,
                        templateName: template.templateName || '',
                        header: template.header || '',
                        body: template.body || '',
                        active: template.active || false
                    });
                } else {
                    setMessage({ text: "Mallen hittades inte.", type: 'error' });
                }
            } catch (error) {
                setMessage({ text: "Misslyckades med att ladda mallen.", type: 'error' });
            }
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        if (id) {
            init(id);
        } else {
            setLoading(false);
        }
    }, [id, init]);

    const validate = () => {
        if (emailTemplate.templateName.trim().length < 1) {
            setMessage({ text: "Mallnamn får inte vara tomt.", type: 'error' });
            return false;
        }
        if (emailTemplate.header.trim().length < 1) {
            setMessage({ text: "Ämne får inte vara tomt.", type: 'error' });
            return false;
        }
        return true;
    };

    const onSubmit = async () => {
        setMessage({ text: "", type: 'error' });
        if (validate()) {
            setSaving(true); // Disable all buttons
            try {
                const result = await (id
                    ? API.EmailTemplates.UpdateEmailTemplate(id, emailTemplate.templateName, emailTemplate.header, emailTemplate.body, emailTemplate.active)
                    : API.EmailTemplates.PostEmailTemplate(emailTemplate.templateName, emailTemplate.header, emailTemplate.body, emailTemplate.active));
                console.log(result.code);
                if (result.code === "OK") {
                    setMessage({ text: "Mallen sparades!", type: 'success' });
                    // Buttons remain disabled, success auto-redirect after 3 seconds
                    setTimeout(() => navigate(`/email-templates`), 3000);
                } else if (result.code === "CONFLICT") {
                    // Re-enable buttons if saving fails
                    setMessage({ text: "En mall med detta namn finns redan.", type: 'error' });
                    setSaving(false);
                } else {
                    setMessage({ text: result.message, type: 'error' });
                    setSaving(false);
                }
            } catch (error) {
                // Re-enable buttons on error
                setMessage({ text: "Misslyckades med att spara mallen.", type: 'error' });
                setSaving(false);
            }
        }
    };

    const handleSendTestEmail = async () => {
        if (emailTemplate.header.trim().length < 1 || emailTemplate.body.trim().length < 1) {
            setMessage({ text: "E-postämne och innehåll får inte vara tomt.", type: 'error' });
            return;
        }

        const confirmSend = window.confirm("Är du säker på att du vill skicka test e-posten till dig själv?");
        if (!confirmSend) {
            return; // Do not proceed if the user cancels the action
        }

        setSaving(true); // Disable buttons
        try {
            await sendTestEmail(emailTemplate.header, emailTemplate.body);
            setMessage({ text: "Test-e-post skickad!", type: 'success' });
        } catch (error) {
            console.error("Misslyckades med att skicka test e-post:", error);
            setMessage({ text: "Misslyckades med att skicka test e-post.", type: 'error' });
        } finally {
            setSaving(false); // Re-enable buttons after sending the email
        }
    };

    const handleInputChange = (field: keyof EmailTemplate, value: string | boolean) => {
        setEmailTemplate(prev => ({ ...prev, [field]: value }));
    };

    if (isLoading) return <Spinner animation="border" />;

    return (
        <div className="create-email-template-page">
            <Stack gap={2}>
                <h1 className="page-title">
                    {id ? "Redigera e-postmall" : "Skapa e-postmall"}
                </h1>

                {message.text && (
                    <div className={`${message.type}-message`}>
                        {message.text}
                    </div>
                )}

                <FormGroup className="template-form-group">
                    <Form.Label className="form-label">Mallnamn</Form.Label>
                    <div className="form-group-row">
                        <Form.Control
                            className="template-name-input"
                            type="text"
                            placeholder="Ange mallnamn"
                            value={emailTemplate.templateName}
                            onChange={(e) => handleInputChange('templateName', e.target.value)}
                            disabled={isSaving}
                        />
                        <div className="switch-container">
                            <Switch
                                checked={emailTemplate.active}
                                onChange={(checked) => handleInputChange('active', checked)}
                                disabled={isSaving}
                                onColor="#173c53"
                                offColor="#878686"
                                uncheckedIcon={false}
                                checkedIcon={false}
                                height={24}
                                width={48}
                            />
                            <Form.Label className="form-label switch-label">Sätt som aktiv</Form.Label>
                        </div>
                    </div>
                </FormGroup>

                <FormGroup className="template-form-group">
                    <Form.Label className="form-label">E-postämne</Form.Label>
                    <Form.Control
                        className="header-input"
                        type="text"
                        placeholder="Ange e-postämne"
                        value={emailTemplate.header}
                        onChange={(e) => handleInputChange('header', e.target.value)}
                        disabled={isSaving}
                    />
                </FormGroup>

                <FormGroup className="template-form-group">
                    <Form.Label className="form-label">E-postinnehåll</Form.Label>
                    <RTE
                        onChange={(newVal: string) => handleInputChange('body', newVal)}
                        value={emailTemplate.body}
                        disabled={isSaving}
                    />
                </FormGroup>

                <Stack direction="horizontal" gap={3} className="button-stack ms-auto">
                    <button className="btn button-cancel" onClick={() => navigate(`/email-templates`)} disabled={isSaving}>
                        <FontAwesomeIcon icon={faBan} style={{ marginRight: 8 }} />
                        Avbryt
                    </button>
                    <button className="btn" onClick={onSubmit} disabled={isSaving}>
                        <FontAwesomeIcon icon={faSave} style={{ marginRight: 8 }} />
                        Spara
                    </button>
                    <button className="btn button-submit" onClick={handleSendTestEmail} disabled={isSaving}>
                        <FontAwesomeIcon icon={faPaperPlane} style={{ marginRight: 8 }} />
                        Skicka test-e-post
                    </button>
                </Stack>

            </Stack>
        </div>
    );
};

export default CreateEmailTemplatePage;
